// Module-level UI store for the history/review plugin.
//
// Cross-seam signalling that would otherwise need prop threading through the
// workbench: the `contextMenu` "Comment here" closure and the "Propose" command
// live OUTSIDE the review panel, but must reach it (enter pin mode, open the
// propose modal, target a node comment). They write here; the review tab +
// provider read via the hooks below. Mirrors the app-shell `diffModeStore`
// idiom (module singleton + `useSyncExternalStore`).
import { useSyncExternalStore } from "react";

interface HistoryState {
  /** Review panel is in comment-pin mode (click a node → new node comment). */
  readonly reviewPinMode: boolean;
  /** The Propose modal is requested open (by the "Propose changes" command).
   *  route.tsx owns the modal (it needs the workspace store's dirty set, which
   *  a plugin provider — folded outside ViewerProviders — can't read) and reads
   *  this flag to open it. */
  readonly proposeOpen: boolean;
  /** "New proposal" command intent — route.tsx navigates to a fresh draft and
   *  acks. A counter (not a bool) so repeat invocations always re-fire. */
  readonly newProposalNonce: number;
  /** A node the user asked to comment on ("Comment here" context menu) — the
   *  review overlay reads it to open its composer at that node, then clears it. */
  readonly pendingNodeComment: string | null;
}

let _state: HistoryState = {
  reviewPinMode: false,
  proposeOpen: false,
  newProposalNonce: 0,
  pendingNodeComment: null,
};
const _subs = new Set<() => void>();

function set(patch: Partial<HistoryState>): void {
  const next = { ..._state, ...patch };
  if (
    next.reviewPinMode === _state.reviewPinMode &&
    next.proposeOpen === _state.proposeOpen &&
    next.newProposalNonce === _state.newProposalNonce &&
    next.pendingNodeComment === _state.pendingNodeComment
  ) {
    return;
  }
  _state = next;
  _subs.forEach((fn) => fn());
}

export function setReviewPinMode(on: boolean): void {
  set({ reviewPinMode: on });
}
export function toggleReviewPinMode(): void {
  set({ reviewPinMode: !_state.reviewPinMode });
}
export function setProposeOpen(open: boolean): void {
  set({ proposeOpen: open });
}
/** "New proposal" command → route.tsx navigates to a fresh unnamed draft. */
export function requestNewProposal(): void {
  set({ newProposalNonce: _state.newProposalNonce + 1 });
}
/** "Comment here" on a node: enter pin mode + queue the node for the composer. */
export function startNodeComment(moduleId: string): void {
  set({ reviewPinMode: true, pendingNodeComment: moduleId });
}
export function clearPendingNodeComment(): void {
  set({ pendingNodeComment: null });
}

function subscribe(fn: () => void): () => void {
  _subs.add(fn);
  return () => _subs.delete(fn);
}

export function useHistoryStore(): HistoryState {
  return useSyncExternalStore(subscribe, () => _state);
}

/** Read the current state without a React render (tests + the route's imperative
 *  intents). The returned object is frozen-by-convention — mutate via setters. */
export function getHistoryState(): HistoryState {
  return _state;
}
